// API Configuration - All endpoints used in the application
// Environment variables are loaded by env-cmd from environment files

// Default to "/api" so CRA dev proxy can handle CORS in development
const LOTTO_API_BASE = process.env.REACT_APP_LOTTO_API_BASE_URL || '/api/v1';
const INTEGRATION_API_BASE = process.env.REACT_APP_INTEGRATION_API_BASE_URL ;
const API_VERSION_PATH_PATTERN = /\/api\/v1\/?$/i;

const getStaticFileBaseUrl = (apiBase: string | undefined) => {
  const normalizedApiBase = apiBase?.trim();

  if (!normalizedApiBase) {
    return undefined;
  }

  try {
    const url = new URL(normalizedApiBase);
    url.pathname = url.pathname.replace(API_VERSION_PATH_PATTERN, '').replace(/\/+$/, '');
    url.search = '';
    url.hash = '';
    return `${url.origin}${url.pathname}`;
  } catch {
    const relativeBase = normalizedApiBase.replace(API_VERSION_PATH_PATTERN, '').replace(/\/+$/, '');
    return relativeBase || undefined;
  }
};

const STATIC_FILE_BASE_URL = getStaticFileBaseUrl(LOTTO_API_BASE);

const buildAbsoluteUrl = (base: string | undefined, postfix: string) => {
  if (!base) return postfix;
  try {
    return new URL(postfix, base).toString();
  } catch {
    return `${base}${postfix}`;
  }
};

const buildStoreEventLogUrl = (
  base: string | undefined,
  storeId: string,
  limit: number,
  offset: number,
  startDate?: string,
  endDate?: string,
  eventType?: string,
  eventCategory?: string,
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (startDate) {
    params.set('start_date', startDate);
  }

  if (endDate) {
    params.set('end_date', endDate);
  }

  if (eventType) {
    params.set('event_type', eventType);
  }

  if (eventCategory) {
    params.set('event_category', eventCategory);
  }

  return `${base}/stores/${storeId}/event-log?${params.toString()}`;
};

const API_URLS = {
  // Local API Endpoints (Backend)
  GET_ALL_GAMES: `${LOTTO_API_BASE}/games`,
  GET_GAMES_MISSING_PLU: `${LOTTO_API_BASE}/games/missing-plu`,
  GET_MISSING_GAMES: `${LOTTO_API_BASE}/games/missing`,
  GET_PLU_VERIFICATION_PENDING_GAMES: `${LOTTO_API_BASE}/games/plu-verification-pending`,
  GET_MISSING_IMAGE_GAMES: `${LOTTO_API_BASE}/games/missing-images`,
  GET_GAME_METADATA_MISMATCHES: `${LOTTO_API_BASE}/games/metadata-mismatches`,
  CREATE_GAME: `${LOTTO_API_BASE}/games`,
  UPDATE_GAME: (gameId: string) => `${LOTTO_API_BASE}/games/by-id/${gameId}`,
  DELETE_GAME: `${LOTTO_API_BASE}/games`,

  GET_ALL_STATES: `${LOTTO_API_BASE}/states`,
  UPDATE_STATE: (stateCode: string) => `${LOTTO_API_BASE}/states/${encodeURIComponent(stateCode)}`,
  
  // Production API Endpoints (Lotto-Reco)
  REQUEST_OTP: `${LOTTO_API_BASE}/auth/request-otp`,
  VERIFY_OTP: `${LOTTO_API_BASE}/auth/verify-otp`,
  REFRESH_TOKEN: `${LOTTO_API_BASE}/auth/refresh`,
  GET_STORES: `${LOTTO_API_BASE}/stores/`,
  GET_ADMIN_STORES: `${LOTTO_API_BASE}/stores/admin`,
  GET_STORE_OWNERS: `${LOTTO_API_BASE}/stores/owners`,
  GET_STORE_OWNER_STORES: (ownerId: string) => `${LOTTO_API_BASE}/stores/owners/${ownerId}/stores`,
  DELETE_USER_WITHOUT_STORES: (userId: string) => `${LOTTO_API_BASE}/users/${userId}`,
  UPDATE_STORE: (storeId: string) => `${LOTTO_API_BASE}/stores/${storeId}`,
  SYNC_GAMES_TO_POS: (storeId: string) => `${LOTTO_API_BASE}/stores/${storeId}/sync-games-to-pos`,
  GET_POS_TYPES: `${LOTTO_API_BASE}/stores/pos-types`,
  GET_ACTIVATED_PACKS: (storeId: string, storeLocationId?: string) => {
    const baseUrl = `${LOTTO_API_BASE}/audits/stores/${storeId}/activated-packs`;
    if (!storeLocationId) {
      return baseUrl;
    }

    const searchParams = new URLSearchParams({
      store_location_id: storeLocationId,
    });

    return `${baseUrl}?${searchParams.toString()}`;
  },
  GET_ACTIVE_GAMES_AND_PACKS: (storeId: string) => 
    `${LOTTO_API_BASE}/audits/stores/${storeId}/activated-packs-by-game`,
  GET_LOTTERY_PACKS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/lottery/packs`,
  ACTIVATE_PACK: `${INTEGRATION_API_BASE}/lottery/activation`,
  SALE: `${INTEGRATION_API_BASE}/pos/sale`,
  RETURN: `${INTEGRATION_API_BASE}/pos/return`,
  RETURN_PACK: `${INTEGRATION_API_BASE}/lottery/return`,
  
  // Payout API Endpoints
  LOTTERY_PAYOUT: `${INTEGRATION_API_BASE}/lottery/payout`,
  POS_PAYOUT: `${INTEGRATION_API_BASE}/pos/payout`,
  
  // Lotto Sale API Endpoint
  LOTTO_SALE: `${INTEGRATION_API_BASE}/lottery/sale`,
  LOTTO_DATA_BACKFILL: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/lotto-data/backfill`,
  
  // Audit API Endpoints
  AUDIT_RECEIPT_AUDITS: `${LOTTO_API_BASE}/audits/receipt-audits`,
  AUDIT_UPLOAD_RECEIPT: `${LOTTO_API_BASE}/audits/receipt-audits/upload`,
  AUDIT_GET_AUDIT_DETAILS: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}`,
  AUDIT_RECEIPT_AUDIT_NOTES: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/notes`,
  AUDIT_GET_AUDIT_IMAGE: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/image`,
  AUDIT_UPDATE_RECEIPT: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/replace-image`,
  AUDIT_UPDATE_AUDIT_STATUS: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/update-status`,
  AUDIT_UPDATE_VENDING_SALES_STATUS: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-sales/update-status`,
  AUDIT_UPDATE_VENDING_INVENTORY_STATUS: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-inventory/update-status`,
  AUDIT_UPDATE_VENDING_SHIFT_REPORT_STATUS: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-shift-report/update-status`,
  AUDIT_GET_VENDING_INVENTORY_PREVIOUS_DATE: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-inventory/previous-date`,
  AUDIT_GET_VENDING_INVENTORY_MAPPING_CONTEXT: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-inventory/mapping-context`,
  AUDIT_GET_VENDING_INVENTORY_PACK_RECOMMENDATIONS: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/vending-inventory/pack-recommendations`,
  AUDIT_SIMULATE_RECEIPT: (queueId: string) =>
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/simulate`,
  AUDIT_REVERT_TO_READY: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/revert-to-ready`,
  AUDIT_REPROCESS: (queueId: string) => 
    `${LOTTO_API_BASE}/audits/receipt-audits/${queueId}/retry-ocr`,
  AUDIT_GET_MOST_RECENT_DETAILS: (storeId: string) => 
    `${LOTTO_API_BASE}/audits/stores/${storeId}/audits/most-recent-details`,
  AUDIT_CREATE_MANUAL_AUDIT: (storeId: string) => 
    `${LOTTO_API_BASE}/audits/${storeId}`,
  AUDIT_LOOKUP_SUBMISSION_LOG_BY_AUDIT_ID: (auditId: string) =>
    `${LOTTO_API_BASE}/audits/admin/audits/${auditId}/submission-log`,
  AUDIT_RESUBMIT_SUBMISSION_LOG: (submissionLogId: string) =>
    `${LOTTO_API_BASE}/audits/admin/submission-logs/${submissionLogId}/resubmit`,

  USER_DETAILS: `${LOTTO_API_BASE}/users`,
  AUTH_ME_AND_GET_USER_DETAILS: `${LOTTO_API_BASE}/auth/me`,
  STORE_IMAGE_URL: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/logo-image`,
  UPLOAD_STORE_LOGO: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/logo`,
  COMPLETE_STORE_IMAGE_URL: (imageUrlPostfix: string) =>
    `${LOTTO_API_BASE}${imageUrlPostfix}`,
  
  // Admin Stores
  GET_ADMIN_ALL_STORES: `${LOTTO_API_BASE}/stores/all`,

  // Feature flags admin
  ADMIN_FEATURE_FLAGS: `${LOTTO_API_BASE}/admin/feature-flags`,
  ADMIN_STORE_USERS: `${LOTTO_API_BASE}/stores/admin/store-users`,
  ADMIN_FEATURE_FLAG: (featureFlagId: string) =>
    `${LOTTO_API_BASE}/admin/feature-flags/${featureFlagId}`,
  ADMIN_FEATURE_FLAG_STORES: (featureFlagId: string) =>
    `${LOTTO_API_BASE}/admin/feature-flags/${featureFlagId}/stores`,
  ADMIN_FEATURE_FLAG_USERS: (featureFlagId: string) =>
    `${LOTTO_API_BASE}/admin/feature-flags/${featureFlagId}/users`,
  ADMIN_STORE_FEATURE_FLAGS: (storeId: string) =>
    `${LOTTO_API_BASE}/admin/stores/${storeId}/feature-flags`,

  // Store audit reports
  GET_STORE_AUDIT_REPORTS_DATE_RANGE: `${LOTTO_API_BASE}/stores/audit-reports/date-range`,
  GET_STORE_AUDIT_REPORTS_DAILY_EXPANDED: `${LOTTO_API_BASE}/stores/audit-reports/daily/expanded`,
  GET_AUDIT_REPORT_EXPANDED: (storeId: string, auditId: string) =>
    `${LOTTO_API_BASE}/stores/audit-reports/${storeId}/audits/${auditId}/expanded`,
  SEND_STORE_RECONCILIATION_EMAIL: (storeId: string) =>
    `${LOTTO_API_BASE}/audit-detail/stores/${storeId}/send-reconciliation-email`,
  MANUAL_LOTTO_SALES_ENTRY: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/pos/manual/lotto-sales`,
  MANUAL_PAYOUT_ENTRY: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/pos/manual/payout`,
  
  // Store users
  GET_STORE_USERS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/users`,
  GET_STORE_LOCATIONS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/locations`,
  GET_STORE_LOCATION_REGISTERS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/location-registers`,
  GET_CSTORE_REGISTER_INFO: (storeId: string) =>
    `${LOTTO_API_BASE}/cstore/${storeId}/register-info`,
  UPDATE_STORE_LOCATION_REGISTER: (storeId: string, registerId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/location-registers/${registerId}`,
  CREATE_STORE_LOCATION_REGISTER: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/location-registers`,
  CREATE_STORE_LOCATION: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/locations`,
  UPDATE_STORE_LOCATION: (storeId: string, locationId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/locations/${locationId}`,
  ADD_SELF_AS_HIDDEN_MANAGER: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/hidden-manager/me`,
  UPDATE_STORE_USER_VISIBILITY: (storeId: string, userId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/users/${userId}/visibility`,
  UPDATE_STORE_USER_DAILY_RECONCILIATION_EMAIL: (storeId: string, userId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/users/${userId}/daily-reconciliation-email`,
  REMOVE_STORE_USER: (storeId: string, userId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/users/${userId}`,
  GET_STORE_EVENT_LOG: (
    storeId: string,
    limit: number,
    offset: number,
    startDate?: string,
    endDate?: string,
    eventType?: string,
    eventCategory?: string,
  ) => buildStoreEventLogUrl(LOTTO_API_BASE, storeId, limit, offset, startDate, endDate, eventType, eventCategory),
  GET_STORE_ONBOARDING_STATUS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/onboarding-status`,
  GET_STORE_POS_MAPPING_OPTIONS: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/pos-mapping-options`,
  SEND_STORE_ONBOARDING_STATUS_EMAIL: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/onboarding-status/send-email`,
  GET_STORE_NOTIFICATIONS: (storeId: string) =>
    `${LOTTO_API_BASE}/notifications/stores/${storeId}`,
  CREATE_NOTIFICATION: `${LOTTO_API_BASE}/notifications/`,
  // Invite store user
  INVITE_STORE_USER: (storeId: string) =>
    `${LOTTO_API_BASE}/stores/${storeId}/invite`,

  // Store passwords (lottery services credentials)
  GET_STORE_PASSWORDS: (storeId: string) =>
    `${LOTTO_API_BASE}/store-passwords/${storeId}`,
  SAVE_LOTTO_PASSWORD: `${LOTTO_API_BASE}/store-passwords/lotto-password`,
  REVEAL_STORE_PASSWORD: (storeId: string, service: string) =>
    `${LOTTO_API_BASE}/store-passwords/admin/${storeId}/${encodeURIComponent(service)}/password`,
  
  // Agent configuration
  GET_AGENT_CONFIG: (storeId: string) =>
    `${LOTTO_API_BASE}/agent/config/${storeId}`,
  GET_WATCHDOG_CONFIGS: (includeRegistered: boolean) =>
    `${LOTTO_API_BASE}/agent/watchdog/configs?include_registered=${includeRegistered ? 'true' : 'false'}`,
  GET_WATCHDOG_CONFIG: (wifiMacAddress: string) =>
    `${LOTTO_API_BASE}/agent/watchdog/configs/${encodeURIComponent(wifiMacAddress)}`,
  GET_PENDING_WATCHDOG_AGENTS: `${LOTTO_API_BASE}/agent/watchdog/configs/pending`,
  UPDATE_WATCHDOG_CONFIG: (wifiMacAddress: string) =>
    `${LOTTO_API_BASE}/agent/watchdog/configs/${encodeURIComponent(wifiMacAddress)}`,
  // Save / Update agent configuration
  SAVE_AGENT_CONFIG: (storeId: string) =>
    `${LOTTO_API_BASE}/agent/config/${storeId}`,
  // Update agent params (e.g., reset or bind device id)
  UPDATE_AGENT_PARAMS: (storeId: string) =>
    `${LOTTO_API_BASE}/agent/params/${storeId}`,
  // Get agent params (includes runtime metadata/device details)
  GET_AGENT_PARAMS: (storeId: string) =>
    `${LOTTO_API_BASE}/agent/params/${storeId}`,
  // Trigger upload of latest logs via agent health-check API
  UPLOAD_AGENT_LOGS: (storeId: string) =>
    `${LOTTO_API_BASE}/agent-health-check/store/${storeId}/upload-logs`,

  // Store logs
  GET_STORE_LOGS: (storeId: string, limit: number, offset: number) =>
    `${LOTTO_API_BASE}/stores/${storeId}/logs?limit=${limit}&offset=${offset}`,
  COMPLETE_STORE_LOG_URL: (logUrlPostfix: string) =>
    buildAbsoluteUrl(LOTTO_API_BASE, logUrlPostfix),
  COMPLETE_STATIC_FILE_URL: (staticUrlPostfix: string) =>
    buildAbsoluteUrl(STATIC_FILE_BASE_URL, staticUrlPostfix),
  COMPLETE_GAME_IMAGE_URL: (imageUrlPostfix: string) =>
    buildAbsoluteUrl(LOTTO_API_BASE, imageUrlPostfix),

  // Email pipeline admin
  EMAIL_PIPELINE_STATUS: `${LOTTO_API_BASE}/admin/email-pipeline/status`,
  EMAIL_PIPELINE_RECORDS: `${LOTTO_API_BASE}/admin/email-pipeline/records`,
  EMAIL_PIPELINE_RECORD_DETAILS: (messageId: string) =>
    `${LOTTO_API_BASE}/admin/email-pipeline/records/${encodeURIComponent(messageId)}`,
  EMAIL_PIPELINE_PROCESS_MESSAGE: (messageId: string, force: boolean) =>
    `${LOTTO_API_BASE}/admin/email-pipeline/process/message?message_id=${encodeURIComponent(messageId)}&force=${force}`,
  
};

export default API_URLS;
